import { z } from 'zod';

import type {
  CashbackBank,
  CashbackCategory,
  CashbackOffer,
  CashbackSnapshot,
} from '../api/contract';

export type CashbackDirectoryMode = 'categories' | 'banks';
export type CashbackResolvedOffer = CashbackOffer & { bankId: string };

export const bankFormSchema = z.object({
  name: z.string().trim().min(1, 'Укажите название банка').max(60),
  color: z.string().regex(/^#[0-9A-F]{6}$/iu, 'Выберите цвет'),
});

export const categoryFormSchema = z.object({
  name: z.string().trim().min(1, 'Укажите название категории').max(60),
  color: z.string().regex(/^#[0-9A-F]{6}$/iu, 'Выберите цвет'),
});

export function sanitizeCashbackRate(value: string): string {
  const normalized = value.replace(',', '.').replace(/[^\d.]/gu, '');
  const [integer = '', ...fractions] = normalized.split('.');
  const fraction = fractions.join('').slice(0, 2);
  return fractions.length > 0 ? `${integer}.${fraction}` : integer;
}

export function parseCashbackRate(value: string): number {
  const normalized = Number.parseFloat(value.replace(',', '.'));
  if (!Number.isFinite(normalized) || normalized <= 0 || normalized > 100) {
    throw new Error('Кешбэк должен быть больше 0% и не больше 100%');
  }
  return Math.round(normalized * 100);
}

export function formatCashbackRate(rateBps: number): string {
  const percent = rateBps / 100;
  return `${Number.isInteger(percent)
    ? percent
    : percent.toLocaleString('ru-RU', { maximumFractionDigits: 2 })}%`;
}

export function formatRateInput(rateBps: number): string {
  const percent = rateBps / 100;
  return Number.isInteger(percent)
    ? String(percent)
    : percent.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
}

export function getBankMonogram(name: string): string {
  return name.trim().charAt(0).toLocaleUpperCase('ru-RU') || 'Б';
}

export function currentOffers(snapshot: CashbackSnapshot): CashbackResolvedOffer[] {
  return snapshot.programs.flatMap(program =>
    program.offers.map(offer => ({ ...offer, bankId: program.bankId })),
  );
}

export function filterBanks(
  snapshot: CashbackSnapshot,
  query: string,
): Array<{ bank: CashbackBank; offers: CashbackResolvedOffer[] }> {
  const normalized = query.trim().toLocaleLowerCase('ru-RU');
  const categories = new Map(snapshot.categories.map(category => [category.id, category]));
  const offers = currentOffers(snapshot);
  return snapshot.banks
    .map(bank => ({
      bank,
      offers: offers
        .filter(offer => offer.bankId === bank.id)
        .sort((first, second) => second.rateBps - first.rateBps),
    }))
    .filter(
      item =>
        normalized.length === 0 ||
        item.bank.name.toLocaleLowerCase('ru-RU').includes(normalized) ||
        item.offers.some(offer =>
          categories
            .get(offer.categoryId)
            ?.name.toLocaleLowerCase('ru-RU')
            .includes(normalized),
        ),
    )
    .sort((first, second) => first.bank.name.localeCompare(second.bank.name, 'ru-RU'));
}

export function groupCategories(
  snapshot: CashbackSnapshot,
  query: string,
): Array<{
  category: CashbackCategory;
  offers: Array<{ offer: CashbackResolvedOffer; bank: CashbackBank }>;
}> {
  const normalized = query.trim().toLocaleLowerCase('ru-RU');
  const banks = new Map(snapshot.banks.map(bank => [bank.id, bank]));
  const offers = currentOffers(snapshot);
  return snapshot.categories
    .map(category => ({
      category,
      offers: offers
        .filter(offer => offer.categoryId === category.id)
        .flatMap(offer => {
          const bank = banks.get(offer.bankId);
          return bank === undefined ? [] : [{ offer, bank }];
        })
        .sort(
          (first, second) =>
            second.offer.rateBps - first.offer.rateBps ||
            first.bank.name.localeCompare(second.bank.name, 'ru-RU'),
        ),
    }))
    .filter(
      group =>
        group.offers.length > 0 &&
        (normalized.length === 0 ||
          group.category.name.toLocaleLowerCase('ru-RU').includes(normalized) ||
          group.offers.some(item =>
            item.bank.name.toLocaleLowerCase('ru-RU').includes(normalized),
          )),
    )
    .sort((first, second) =>
      first.category.name.localeCompare(second.category.name, 'ru-RU'),
    );
}

export function pluralizeCategories(count: number): string {
  return pluralize(count, 'категория', 'категории', 'категорий');
}

export function pluralizeBanks(count: number): string {
  return pluralize(count, 'банк', 'банка', 'банков');
}

function pluralize(count: number, one: string, few: string, many: string): string {
  const mod100 = count % 100;
  const mod10 = count % 10;
  if (mod100 >= 11 && mod100 <= 14) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}
